import { pick } from "underscore"
import ErrorTrack from "lib/ErrorTrack"
import fetch from "unfetch"

/**
 * Wraps a ProgressEvent so that we can pass it to ErrorTrack with impunity.
 *
 * When a request to `fetch` is blocked, e.g. by AdBlockPlus, it throws a
 * ProgressEvent. If we pass this directly to ErrorTrack.notify,
 * Bugsnag complains because it is not derived from Error. So, we wrap the
 * ProgressEvent in this error and add an informative error message to help
 * us diagnose which URLs donors are blocking.
 * @see https://app.bugsnag.com/the-conversation/frontend-tc-donations/errors/5fbf2b4432e7b70018d3e03e
 */
export class BlockedRequestError extends Error {
  constructor(path, progressEvent) {
    super(`Request to ${path} was blocked`)
    this.path = path
    this.progressEvent = progressEvent
    this.name = this.constructor.name
  }
}

export class UnprocessableEntityError extends Error {
  constructor(url) {
    super(`Call to ${url} was unprocessable - probably due to an invalid auth token`)
    this.url = url
    this.name = this.constructor.name
  }
}

/**
 * A gateway for calling endpoints on our server (TC-Donate)
 */
export default class TcDonateGateway {
  /**
   * Configures the gateway
   * @param authenticityToken
   * @param basePath e.g. "/au"
   */
  constructor({ authenticityToken, basePath, region }) {
    this.authenticityToken = authenticityToken
    this.basePath = basePath
    this.region = region
  }

  /**
   * Calls a JSON endpoint on our server. Wraps a call to JS's `fetch` method -
   * https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API.
   *   n.b. the expected return value is an object with a `success` attribute -
   *     if `success` is true, the call succeeded, if false, it didn't
   * @param path e.g. "donations.json"
   * @param method valid methods for `fetch`, e.g. "POST"
   * @param data an object to be turned into a JSON payload
   * @param basePath an optional base path to override the default
   * @return {Promise<unknown>}
   */
  call(path, method, data, basePath = undefined) {
    const body = JSON.stringify(
      TcDonateGateway.underscoreParams({
        utf8: "✓",
        authenticityToken: this.authenticityToken,
        region: this.region,
        ...data,
      })
    )
    const headers = { "content-type": "application/json" }
    const fullPath = `${basePath || this.basePath}/${path}`

    return fetch(fullPath, { method, headers, body })
      .then((result) => {
        if (!result.ok) {
          const resultErrors = pick(result, [
            "ok",
            "redirected",
            "status",
            "statusText",
            "type",
            "url",
          ])
          let error
          if (resultErrors.status === 422) {
            error = new UnprocessableEntityError(resultErrors.url)
          } else {
            error = new Error(
              `TcDonateGateway.call - request failed: ${JSON.stringify(resultErrors)}`
            )
          }
          return Promise.reject(error)
        }
        return result.json()
      })
      .catch((error) => {
        let processedError = error
        if (error instanceof ProgressEvent) {
          processedError = new BlockedRequestError(fullPath, error)
        }
        // Unprocessable entity errors result from incorrect auth tokens, which
        // are not something we can generally fix. Notify Bugsnag of any other
        // type of error
        if (!(error instanceof UnprocessableEntityError)) {
          ErrorTrack.notify(processedError)
        }
        return Promise.reject(processedError)
      })
      .then(
        (result) =>
          new Promise((resolve, reject) => {
            if (result.success) {
              resolve(result)
            } else {
              reject(result)
            }
          })
      )
  }

  /**
   * Returns a copy of `object` with all its camel-case keys converted to
   *   underscore case, recursively.
   * @param object
   * @return {*}
   * @see https://stackoverflow.com/questions/30521224/javascript-convert-pascalcase-to-underscore-case
   */
  static underscoreParams(object) {
    const output = {}
    let underscoredKey
    Object.entries(object).forEach(([key, value]) => {
      let newValue = value
      if (newValue && typeof newValue === "object" && !Array.isArray(newValue)) {
        newValue = TcDonateGateway.underscoreParams(newValue)
      }
      underscoredKey = key
        .replace(/\.?([A-Z]+)/g, (x, y) => `_${y.toLowerCase()}`)
        .replace(/^_/, "")
      output[underscoredKey] = newValue
    })
    return output
  }
}
